import { useState, useEffect } from 'react';

export type ScreenSize = 'S' | 'M' | 'L' | 'XL';

export interface ScreenSizeInfo {
  width: number;
  height: number;
  size: ScreenSize;
  isMobile: boolean;
  isS: boolean;
  isM: boolean;
  isL: boolean;
  isXL: boolean;
}

const getScreenSize = (width: number): ScreenSize => {
  if (width < 768) return 'S';
  if (width >= 768 && width < 992) return 'M';
  if (width >= 992 && width < 1200) return 'L';
  return 'XL';
};

export const useScreenSize = (): ScreenSizeInfo => {
  const [screenInfo, setScreenInfo] = useState<ScreenSizeInfo>(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const height = typeof window !== 'undefined' ? window.innerHeight : 768;
    const size = getScreenSize(width);

    return {
      width,
      height,
      size,
      isMobile: width < 768,
      isS: size === 'S',
      isM: size === 'M',
      isL: size === 'L',
      isXL: size === 'XL',
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId: number | undefined;

    const handleResize = () => {
      // Debounce the resize event slightly to avoid too many re-renders
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const size = getScreenSize(width);

        setScreenInfo({
          width,
          height,
          size,
          isMobile: width < 768,
          isS: size === 'S',
          isM: size === 'M',
          isL: size === 'L',
          isXL: size === 'XL',
        });
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  return screenInfo;
};

export default useScreenSize;
